'use client'

/**
 * Greeting Losi: logo intro, then settle into idle.
 * Every click → jiggle. Five clicks in a window also open Losi Games.
 */

import { useCallback, useEffect, useRef, useState, type MouseEvent } from 'react'

import { useLosiGamesOptional } from '@/components/losi-games/losi-games-provider'
import { LosiAvatar } from '@/components/losi-bot/losi-avatar'
import { LOGO_INTRO } from '@/lib/losi-engine/losi'
import type { StateId } from '@/lib/losi-engine/states'
import { LOSI_JIGGLE_MS, losiMarkTapOrigin } from '@/lib/losi-games/mark-tap'
import { cn } from '@/lib/utils'

const INTRO_HOLD_MS = Math.round(LOGO_INTRO * 1000) + 350

export interface GreetingLosiMarkProps {
  size?: number
  className?: string
  shape?: string
  hoverState?: StateId | null
}

export function GreetingLosiMark({
  size = 45,
  className,
  shape,
  hoverState = null
}: GreetingLosiMarkProps) {
  const games = useLosiGamesOptional()
  const [state, setState] = useState<StateId>('logo')
  const [jiggling, setJiggling] = useState(false)
  const jiggleTimer = useRef(0)

  useEffect(() => {
    const timer = window.setTimeout(() => setState('idle'), INTRO_HOLD_MS)
    return () => window.clearTimeout(timer)
  }, [])

  useEffect(() => () => window.clearTimeout(jiggleTimer.current), [])

  const onClick = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      games?.registerLosiTap(losiMarkTapOrigin(e))
      window.clearTimeout(jiggleTimer.current)
      setJiggling(true)
      jiggleTimer.current = window.setTimeout(() => setJiggling(false), LOSI_JIGGLE_MS)
    },
    [games]
  )

  const shown: StateId = jiggling ? 'jiggle' : state

  return (
    <LosiAvatar
      state={shown}
      size={size}
      shape={shape}
      fitBody
      hoverState={jiggling ? null : hoverState}
      className={cn(className)}
      onClick={onClick}
      title="Click to jiggle"
    />
  )
}
